import React, { type PropsWithChildren } from 'react';
import { SwitchCase } from '@toss/react';
import { Blue200, ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { AuthorTag } from '@/components/AuthorTag';
import { NoSSR } from '@/components/NoSSR';
import { useViewport } from '@/hooks';
import { useFormatLargeNumber } from '@/hooks/number/useLargeNumber';
import { FolderFillIcon, OptionVerticalIcon, PlayIcon, QuestionmarkSmallBubbleIcon, SquareStackIcon } from '@/icons';
import { Chip, PublisherChip, SchoolInfoChip } from '@/shared/components/Chip/QuizInfoChip';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { useIsInLti } from '@/shared/lti';
import { type QuizListItemProps, QuizListItemProvider, useQuizListItemProvider } from '@/shared/quizList/hooks';
import { useQuizCardEventTrack } from '@/shared/quizList/hooks/useQuizCardEventTrack';
import { useQuizListItemAction } from '@/shared/quizList/hooks/useQuizListItemAction';
import { QuizListItemThumbnail } from '@/shared/quizList/QuizListItem/QuizListItemThumbnail';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { Button } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { HighlightedSearchText } from './HighlightedSearchText';
import { LtiFooterActions } from './LtiFooterActions';
import { MatchedQuestions } from './MatchedQuestions';
import { QuizListItemHoverButton } from './QuizListItemHoverButton';
export const QuizListItem = (props: QuizListItemProps) => {
  const {
    quizSet,
    menuItems: _menuItems
  } = props;
  const {
    isPortable
  } = useViewport();
  const matchedQuestionSnippets = quizSet.matchedQuestionSnippets ?? [];
  const matchedQuestions = quizSet.matchedQuestions ?? [];
  const hasMatchedQuestions = matchedQuestionSnippets.length > 0 || matchedQuestions.length > 0 || Boolean(quizSet.matchedQuestionHighlights?.length);
  const menuItems = _menuItems.filter(d => d.hidePortableDevice ? !isPortable : true);
  return <QuizListItemProvider {...props} quizSet={quizSet} menuItems={menuItems} data-sentry-element="QuizListItemProvider" data-sentry-component="QuizListItem" data-sentry-source-file="QuizListItem.tsx">
      <CardWrapper data-sentry-element="CardWrapper" data-sentry-source-file="QuizListItem.tsx">
        <QuizListItemThumbnail data-sentry-element="QuizListItemThumbnail" data-sentry-source-file="QuizListItem.tsx" />
        <div className={'flex size-full  items-center border-b  border-b-alternative'}>
          <div className={cn('flex w-full flex-col gap-xs', hasMatchedQuestions ? 'max-tablet-lg:gap-3xs' : 'max-mobile-lg:gap-sm max-tablet-lg:gap-sm', 'max-tablet-lg:py-md py-lg max-tablet-lg:min-h-[116px] min-h-[157px]')}>
            <CardContentTop data-sentry-element="CardContentTop" data-sentry-source-file="QuizListItem.tsx" />
            {hasMatchedQuestions && <div className={'flex w-full flex-col gap-3xs max-tablet-lg:py-2xs'}>
                <MatchedQuestions snippets={matchedQuestionSnippets} legacyQuestions={matchedQuestions} legacyHighlights={quizSet.matchedQuestionHighlights} />
              </div>}
            <CardContentBottom data-sentry-element="CardContentBottom" data-sentry-source-file="QuizListItem.tsx" />
          </div>
        </div>
      </CardWrapper>
    </QuizListItemProvider>;
};
const CardWrapper = (props: PropsWithChildren) => {
  const {
    quizSet
  } = useQuizListItemProvider();
  const {
    trackEnter
  } = useQuizCardEventTrack();
  return <Link prefetch={false} target={'_self'} href={`/quiz/${quizSet.id}`} className={cn('group relative flex w-full cursor-pointer items-center max-tablet-lg:gap-sm gap-md px-3xs py-none hover:bg-neutral ')} onClick={event => {
    if ((event.target as HTMLDivElement)?.getAttribute('role') === 'menu') {
      event.preventDefault();
      return;
    }
    trackEnter();
  }} data-sentry-element="Link" data-sentry-component="CardWrapper" data-sentry-source-file="QuizListItem.tsx">
      {props.children}
    </Link>;
};
const CardContentTop = () => {
  const {
    quizSet,
    alwaysDropDownMenu,
    subjectName
  } = useQuizListItemProvider();
  const {
    isPortable
  } = useViewport();
  const isInExternalPlatform = useIsInExternalPlatform();
  const inLti = useIsInLti();
  const isShowDropDown = !isInExternalPlatform && !inLti && (alwaysDropDownMenu ? true : isPortable);
  return <div className={'flex flex-col gap-2xs pr-[20px] max-tablet-lg:gap-3xs max-tablet-lg:pr-none'} data-sentry-component="CardContentTop" data-sentry-source-file="QuizListItem.tsx">
      <div className={'flex w-full justify-between gap-2xs max-tablet-sm:h-[32px] max-tablet-sm:items-center max-tablet-sm:gap-3xs'}>
        <div className={'flex flex-wrap gap-3xs'}>
          {quizSet.categoryInfo && <SchoolInfoChip className={'text-body-sm-semibold max-tablet-lg:px-2xs max-tablet-lg:text-caption-md-semibold'} schoolInfo={quizSet.categoryInfo} />}
          {subjectName && <Chip className={'text-body-sm-semibold max-tablet-lg:px-2xs max-tablet-lg:text-caption-md-semibold'}>
              {subjectName}
            </Chip>}
          <PublisherChip className={'text-body-sm-semibold max-tablet-lg:px-2xs max-tablet-lg:text-caption-md-semibold'} hideEtc={true} publisherId={quizSet.categoryInfo?.publisher} data-sentry-element="PublisherChip" data-sentry-source-file="QuizListItem.tsx" />
        </div>
        <NoSSR data-sentry-element="NoSSR" data-sentry-source-file="QuizListItem.tsx">{isShowDropDown && <CardDropDownMenu />}</NoSSR>
      </div>
      <div className="word-break-all line-clamp-2 max-w-full text-body-lg-bold tablet-sm:text-heading-sm-bold">
        <HighlightedSearchText text={quizSet.title} highlightSegments={quizSet.titleHighlights} data-sentry-element="HighlightedSearchText" data-sentry-source-file="QuizListItem.tsx" />
      </div>
    </div>;
};
const CardDropDownMenu = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    quizSet,
    menuItems
  } = useQuizListItemProvider();
  const {
    onClickEvent
  } = useQuizListItemAction(quizSet);
  if (!menuItems.length) return null;
  return <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-component="CardDropDownMenu" data-sentry-source-file="QuizListItem.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="QuizListItem.tsx">
        <Button variant={'transparent'} intent={'neutral'} size={'xs'} data-sentry-element="Button" data-sentry-source-file="QuizListItem.tsx">
          <OptionVerticalIcon size={'xs'} data-sentry-element="OptionVerticalIcon" data-sentry-source-file="QuizListItem.tsx" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={'py-3xs'} align={'end'} data-sentry-element="DropdownMenuContent" data-sentry-source-file="QuizListItem.tsx">
        {menuItems.map((item, index) => <DropdownMenuItem disabled={item.disabled} key={index} onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        onClickEvent(item.onClickEventName);
      }} className={'flex gap-3xs'}>
            {<item.icon size={'xs'} fill={ICON_COLOR.default} className={'shrink-0'} />}
            <span className={'text-caption-lg-medium'}>
              {item.rawLabel ?? t(item.label)}
            </span>
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
};
const CardContentBottom = () => {
  const {
    quizSet,
    isHideAuthor,
    menuItems
  } = useQuizListItemProvider();
  const {
    isPortable,
    isMobile
  } = useViewport();
  const isInExternalPlatform = useIsInExternalPlatform();
  const inLti = useIsInLti();
  return <div className="w-full" data-sentry-component="CardContentBottom" data-sentry-source-file="QuizListItem.tsx">
      <div className={`w-full laptop-sm:min-h-[40px] ${isPortable ? 'flex items-center' : 'flex flex-col gap-2xs'}`}>
        {/*
          시안(33390:114316)은 작성자와 폴더 배지를 **한 줄**에 두고 그 아래 문제수·재생수를
          놓는다. 데스크톱 세로 스택에서 둘이 각자 줄을 차지하지 않도록 묶는다.
         */}
        <div className={'flex min-w-px items-center gap-2xs'}>
          {!isHideAuthor && <AuthorTag authorName={quizSet.authorUsername} isOfficial={quizSet.isOfficial} authorHashId={quizSet.authorId} className="max-mobile-lg:text-caption-lg-medium" iconSize={isMobile ? 'xs' : 'sm'} />}
          <FolderTag hasAuthorBefore={!isHideAuthor} data-sentry-element="FolderTag" data-sentry-source-file="QuizListItem.tsx" />
        </div>
        <MetaInfos data-sentry-element="MetaInfos" data-sentry-source-file="QuizListItem.tsx" />
      </div>
      <NoSSR data-sentry-element="NoSSR" data-sentry-source-file="QuizListItem.tsx">
        {inLti ? <LtiFooterActions quizSet={quizSet} menuItems={menuItems} /> : !isPortable && !isInExternalPlatform && <Buttons />}
      </NoSSR>
    </div>;
};

/**
 * 이 퀴즈가 속한 폴더. 내 퀴즈 검색 결과에만 실려 온다(`folderId`·`folderName`).
 *
 * 폴더 안 퀴즈는 기본 목록에서 빠지고 **검색·필터를 걸었을 때만** 함께 나오므로,
 * 어디에 있는 퀴즈인지 여기서 알려주고 누르면 그 폴더 상세로 보낸다.
 */
const FolderTag = ({
  hasAuthorBefore
}: {
  hasAuthorBefore: boolean;
}) => {
  const {
    quizSet
  } = useQuizListItemProvider();
  if (!quizSet.folderId || !quizSet.folderName) return null;
  return <>
      {/*
        작성자와 폴더명을 가르는 세로선(시안 SVG: 1px · 12px · #F0F2F7).
        작성자가 숨겨진 목록(내 퀴즈)에서는 앞에 가를 것이 없으니 그리지 않는다.
       */}
      {hasAuthorBefore && <span aria-hidden className={'h-sm w-0 shrink-0 border-l border-assistive'} />}
      <Link href={`/folder/${quizSet.folderId}`}
    /* 카드 전체가 퀴즈 상세로 가는 링크라, 폴더로 가는 이 링크는 전파를 끊는다. */ onClick={e => e.stopPropagation()} className={'flex shrink-0 items-center gap-3xs text-body-md-medium text-neutral hover:underline'} data-sentry-element="Link" data-sentry-source-file="QuizListItem.tsx">
        <FolderFillIcon size={'xs'} fill={Blue200} data-sentry-element="FolderFillIcon" data-sentry-source-file="QuizListItem.tsx" />
        <span className={'max-w-[160px] truncate'}>{quizSet.folderName}</span>
      </Link>
    </>;
};
const MetaInfos = () => {
  const {
    quizSet
  } = useQuizListItemProvider();
  const {
    isMobileSm,
    isPortable
  } = useViewport();
  return <div className={`flex w-full items-center justify-start max-tablet-lg:justify-end ${isPortable ? 'gap-xs' : 'gap-md'}`} data-sentry-component="MetaInfos" data-sentry-source-file="QuizListItem.tsx">
      <QuizMetaInfo type={'quizCount'} quizSet={quizSet} data-sentry-element="QuizMetaInfo" data-sentry-source-file="QuizListItem.tsx" />
      <QuizMetaInfo type={'playCount'} quizSet={quizSet} data-sentry-element="QuizMetaInfo" data-sentry-source-file="QuizListItem.tsx" />
      <QuizMetaInfo className={isMobileSm ? 'hidden' : ''} type={'copyCount'} quizSet={quizSet} isHideZeroNumber={true} data-sentry-element="QuizMetaInfo" data-sentry-source-file="QuizListItem.tsx" />
    </div>;
};
const Buttons = () => {
  const {
    t
  } = useTranslation(['common']);
  const {
    quizSet,
    menuItems
  } = useQuizListItemProvider();
  const {
    onClickEvent
  } = useQuizListItemAction(quizSet);
  return <div className={'shrink-1 absolute bottom-[24px] right-[20px] flex gap-2xs '} data-sentry-component="Buttons" data-sentry-source-file="QuizListItem.tsx">
      {menuItems.filter(s => !s.excludeHoverMenu).map((item, index) => <QuizListItemHoverButton key={index} variant={item.variant} intent={item.intent} onClick={e => {
      onClickEvent(item.onClickEventName);
      e.stopPropagation();
    }} icon={<item.icon size={'sm'} className={'shrink-0'} />}>
            {item.rawLabel ?? t(item.label)}
          </QuizListItemHoverButton>)}
    </div>;
};
const QuizMetaInfo = (props: PropsWithChildren & {
  type: 'copyCount' | 'playCount' | 'quizCount';
  quizSet: SearchQuizsetRes;
  className?: string;
  isHideZeroNumber?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const formatLargeNumber = useFormatLargeNumber();
  const postFix = props.type === 'quizCount' ? t('common:common.question') : '';
  if (props.isHideZeroNumber && !props.quizSet[props.type]) {
    return null;
  }
  return <div className={cn('flex min-w-[32px] shrink-0 items-center gap-3xs text-body-sm-regular text-alternative', props.className)} data-sentry-component="QuizMetaInfo" data-sentry-source-file="QuizListItem.tsx">
      <SwitchCase value={props.type} caseBy={{
      quizCount: <QuestionmarkSmallBubbleIcon size={'2xs'} />,
      playCount: <PlayIcon size={'2xs'} />,
      copyCount: <SquareStackIcon size={'2xs'} />
    }} data-sentry-element="SwitchCase" data-sentry-source-file="QuizListItem.tsx" />
      <span>
        {formatLargeNumber(props.quizSet[props.type])}
        {postFix}
      </span>
    </div>;
};